import {ConfigObjectType, enumContainer} from '@wallaby-ui/core';
import {useFormatters} from '@wallaby-ui/document';
import {useCallback} from 'react';

import {useAppSelector} from '../../../store_hooks';
import {useValueListField} from './useValueListField';

/* Invasive devices are now a single merged value-list (PDH-3128): the BE stores/serves one string[] where
   predefined devices are carried as their enum NAME (e.g. "INTUBATION") mixed with free-text user values.
   This is the same raw-text value-list mechanism as surgery types / user tags, with ONE difference: known
   predefined values must display their translated label (via the InvasiveDevicePredefined enum), while
   free-text values render as-is. The old predefined/userDefined split + PREDEFINED_INPUT_SUFFIX trick is
   gone - there is no split to encode anymore. */

/** Resolver: predefined invasive-device enum NAME -> translated label; any other (free-text) value -> raw. */
export function useInvasiveDeviceLabel() {
  const enumMap = useAppSelector((state) => (state[enumContainer.ENUM_REDUCER_STATE_ID] as any).enumMap);
  const {t} = useFormatters();
  return useCallback(
    (value: string): string => {
      const def = (enumMap.InvasiveDevicePredefined ?? []).find((e: any) => e.name === value);
      return def ? t(def.messageKey) : value;
    },
    [enumMap, t]
  );
}

export type UseInvasiveDevicesProps = {
  config: ConfigObjectType;
};

export function useInvasiveDevices({config}: UseInvasiveDevicesProps) {
  const getInvasiveDeviceLabel = useInvasiveDeviceLabel();
  const {options, isLoading, refetch} = useValueListField({
    config,
    url: config.service.url.invasiveDeviceValues,
    getLabel: getInvasiveDeviceLabel,
  });

  return {
    invasiveDeviceOptions: options,
    invasiveDeviceOptionsLoading: isLoading,
    refetchInvasiveDeviceValues: refetch,
    getInvasiveDeviceLabel,
  };
}
